import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import { AlertCircle } from 'lucide-react';

import { Case } from '@/api/cases';
import { parseDateString } from '@/utils/dates';
import { formatId } from '@/utils/strings';
import { CASE_STATUS_TEXT, isCaseSubmitted } from '@/utils/cases';
import { ROUTES } from '@/router/routes';
import { useUserProfile } from '@/states/user-profile';

import { Table } from '../ui/table';
import { GradesCaseTable } from './grades-case-table';
import { SegmentationInsightsImages } from '../insights/segmentation-images';

type TableMode = 'cases' | 'insights';

type CasesTableProps = {
  cases: Case[];
  mode?: TableMode;
  enableEditAction?: boolean;
};

export function CasesTable({ cases, mode = 'cases', enableEditAction = false }: CasesTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    profile: { role: currentRole },
  } = useUserProfile();

  const isCase = (row: unknown): row is Case => {
    return typeof row === 'object' && row !== null && 'case_id' in (row as Record<string, unknown>);
  };

  const formatDate = useCallback(
    (
      date: unknown // format date from '2024-03-14T14:57:21:139969'
    ) => parseDateString(date as string)?.toLocaleDateString() ?? 'Invalid Date',
    []
  );

  const tooltipCaseId = useCallback(
    (case_row: unknown) => (case_row as NonNullable<typeof cases>[number]).case_id,
    []
  );

  const acknowledgeAlert = useCallback(
    (case_row: unknown) => {
      const grades = (case_row as NonNullable<typeof cases>[number]).grades ?? [];
      if (grades.length === 0) return null;

      // we validate if some of the grades needs to acknowledge
      const needsAcknowledgement = grades.some(
        (grade) => grade.needs_acknowledgement && !grade.acknowledged_by
      );

      if (needsAcknowledgement) {
        return (
          <div className="inline-flex gap-2 items-center">
            <span data-tooltip-id="acknowledge-alert" data-tooltip-delay-hide={1000}>
              <AlertCircle className="h-5 w-5 text-red-700" />
            </span>
            <Tooltip
              id="acknowledge-alert"
              place="top"
              className="!bg-red-50 font-medium !text-red-800"
            >
              {t('cases-table-needs-acknowledge')}
            </Tooltip>
          </div>
        );
      }
    },
    [t]
  );

  const shouldHideCollapsible = useCallback((case_row: unknown) => {
    const grades = (case_row as NonNullable<typeof cases>[number]).grades ?? [];
    return grades.length === 0;
  }, []);

  const HEADERS = [
    { label: t('cases-table-case-id'), key: 'case_id', format: formatId, tooltip: tooltipCaseId },
    { label: t('cases-table-patient-id'), key: 'patient_id' },
    { label: t('cases-table-created'), key: 'date_created', format: formatDate },
    ...(currentRole === 'vitaziadmin'
      ? [
          {
            label: 'Result (vitazi-admin only)',
            key: 'result',
            format: (_: unknown, row?: unknown) =>
              isCase(row) ? CASE_STATUS_TEXT[row.result] : null,
          },
        ]
      : []),
    ...(mode !== 'insights'
      ? [
          {
            label: t('cases-table-report'),
            key: 'grades',
            type: 'collapsible' as const,
            value: acknowledgeAlert,
            hide: shouldHideCollapsible,
          },
        ]
      : []),
    ...(mode === 'insights'
      ? [
          {
            label: ' ',
            key: 'insights-action',
            format: (_: unknown, row?: unknown) =>
              isCase(row) ? <SegmentationInsightsImages caseId={row.case_id} /> : null,
          } as const,
        ]
      : []),
    ...(enableEditAction
      ? ([
          {
            label: t('cases-table-actions', 'Actions'),
            key: 'actions',
            type: 'actions' as const,
            edit: (row: unknown) => {
              if (!isCase(row)) return;
              const path = String(ROUTES.CASE_DETAIL).replace(':caseId', String(row.case_id));
              navigate(path);
            },
            disabled: (row: unknown) => (isCase(row) ? isCaseSubmitted(row.result) : true),
          },
        ] as const)
      : []),
  ];

  return <Table headers={HEADERS} data={cases ?? []} CollapsibleComponent={GradesCaseTable} />;
}
